'use client'
import { LayoutProvider } from 'providers/LayoutProvider'
import { HeaderProvider } from 'providers/HeaderProvider'
import styles from 'components/LayoutAppRouter/Layout.module.scss'
import { useCallback, useContext, useEffect, useState, memo } from 'react'
import HeaderContainer from 'components/Header/HeaderContainer/HeaderContainer'
import Cookies from 'js-cookie'
import {
  AHL_PZN_ROUTES_IGNORE,
  VARIATIONS_UPPER,
  ACX_CHATBOT,
  FDR_CHATBOT,
  FEATURE_TOGGLES,
} from 'constants/featureToggles'
import classNames from 'classnames'
import { ultramarine } from 'components/Fonts/Fonts'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import logger from '@achieve/cx-logger'
import { SITE_ENVIRONMENTS } from 'constants/websites'
import { getUserInsights } from '@achieve/cx-event-horizon/browser'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { handleTrackAndReactEvent } from 'utils/analytics'
import { AnalyticsContext } from 'providers/AnalyticsProvider'
import FooterSticky from '../Footer/FooterSticky/FooterSticky'
import FooterContainer from 'components/Footer/FooterContainer/FooterContainer'
import { BannerProvider } from 'providers/BannerProvider'
import { ConsentBannerV2 } from 'components/ConsentBannerV2'
import { FdrPage, BillsPage } from 'utils/environmentConfig'
import { createExperimentObject } from '@/utils/experiments'
import { getFeatureFlag, updateFeatureFlag } from '@/utils/featureFlags'
import { CHATBOT_ANALYTICS_MAPPING } from '@/constants'
import ChatGate from '@/components/Chatbot/ChatGate'
import DirectMailPzn from 'components/DirectMailPzn/DirectMailPzn'
import FooterStickyChatbot from '../Footer/FooterStickyChatbot/FooterStickyChatbot'
import { useLayoutChromeProps } from './useLayoutChromeProps'
import { PhoneDataProvider } from 'providers/PhoneDataProvider'
import { ClientDashboardLoginProvider } from 'providers/ClientDashboardLoginProvider/ClientDashboardLoginProvider'
import ClientDashboardLoginOverlayGate from 'components/ClientDashboardLoginOverlayFdr/ClientDashboardOverlayGate'
const MemoizedHeaderContainer = memo(HeaderContainer)
const MemoizedFooterContainer = memo(FooterContainer)
const MemoizedFooterSticky = memo(FooterSticky)
const MemoizedFooterStickyChatbot = memo(FooterStickyChatbot)
// ConsentBannerV2 is mounted here as a fully client-side component.

/** Avoid re-rendering the page subtree when `Layout` re-renders but `children` reference is unchanged. */
const LayoutMainChildren = memo(function LayoutMainChildren({ children }) {
  return children
})

const makeMainStyle = (
  siteEnv,
  showHeader,
  showProgressPage,
  showDebtConsultantCta,
  isHomePath,
  isLegalSubdomain,
  enableMegaMenu = false
) => {
  if (!showHeader) {
    return 'pt-0'
  }

  if (siteEnv === FdrPage) {
    // MegaMenu lives in document flow and manages its own sticky spacer.
    // Legacy FDR header is `fixed` on non-home routes and needs top padding.
    if (enableMegaMenu || isHomePath) {
      return 'pt-0'
    }

    if (isLegalSubdomain) {
      return `pt-[72px] ${showDebtConsultantCta ? 'lg:pt-[128px]' : 'lg:pt-[96px]'}`
    }

    return `pt-[72px] ${showDebtConsultantCta ? 'lg:pt-[128px]' : 'lg:pt-[96px]'}`
  } else if (siteEnv === BillsPage) {
    return `pt-[48px]`
  } else {
    if (showProgressPage) {
      return 'pt-[100px]'
    }
    return 'pt-[72px] lg:pt-[96px]'
  }
}

/**
 * Layout component for the application routes.
 *
 * Handles the main structural elements of the app including:
 *   - Dynamic header and footer display
 *   - BannerProvider and ConsentBanner integration
 *   - Feature-flagged chatbot and sticky footer experiences
 *   - Analytics/experimentation hooks
 *   - Layout styles based on environment and route specifics
 *
 * Page-level configuration is consumed via PageConfigProvider context
 * to avoid prop drilling through the component tree.
 */
export default function Layout({
  children,
  footer,
  header,
  isMobileUA,
  pageConfig,
  pageDisclosure = '',
  secondaryDisclosures = [],
  showFooter = true,
  showHeader = true,
  alignContentBottom = false,
  consentBanner,
  specificHeaderCtaDataFiltered,
  footerSticky,
  showPhoneNumber,
  siteInfo,
  leadId = '',
  printWebAppSchema = true,
  printLocalBusinessSchema = false,
  associatedProduct,
  publicProcessDotEnv,
  disabledRoutes,
  disabledElements,
  showProgressPage,
  customPhoneData,
  dynamicStickyNav,
  predefinedChatPrompts,
  phoneData,
}) {
  const {
    state: { isPageLoaded: contextIsPageLoaded },
  } = useContext(AnalyticsContext)

  const [isPageLoadedFromEvent, setIsPageLoadedFromEvent] = useState(
    () => !!globalThis.__acxAnalyticsState?.isPageLoaded
  )

  useEffect(() => {
    if (isPageLoadedFromEvent) return

    if (globalThis.__acxAnalyticsState?.isPageLoaded) {
      setIsPageLoadedFromEvent(true)
      return
    }

    const handler = (e) => {
      if (e.detail?.isPageLoaded) setIsPageLoadedFromEvent(true)
    }
    window.addEventListener('acx-analytics-state-change', handler)
    return () => window.removeEventListener('acx-analytics-state-change', handler)
  }, [isPageLoadedFromEvent])

  const isPageLoaded = contextIsPageLoaded || isPageLoadedFromEvent
  const [isReturningUser, setIsReturningUser] = useState(null)
  const [chatbotVariation, setChatbotVariation] = useState(null)
  const onChatbotFlagReady = useCallback((value) => setChatbotVariation(value), [])
  const [isConsentBannerVisible, setConsentBannerVisible] = useState(false)

  const { fireAndForget } = useTriggerEvents()
  useEffect(() => {
    // Only fire event for FDR and Achieve website.
    if (
      header?.siteEnv === SITE_ENVIRONMENTS.FDR ||
      header?.siteEnv === SITE_ENVIRONMENTS.ACHIEVE
    ) {
      async function fetchInsights() {
        // Guard against running before page is fully loaded.
        // Page fully loaded = merkle is ready
        if (!isPageLoaded) return

        try {
          const res = await getUserInsights()
          const segmentType = res?.insights?.profileInsights?.segmentType
          const isNewUser = [
            'repeat_anonymous_visitor',
            'first_time_anonymous_visitor',
            undefined,
          ].includes(segmentType)

          // Check if we have a stored value from previous page load
          const storedIsNewUser = sessionStorage.getItem('isNewUser')

          // If getUserInsights returns empty data but we have a stored value, use it
          let finalIsNewUser = isNewUser
          if (!segmentType && storedIsNewUser !== null) {
            finalIsNewUser = storedIsNewUser === 'true'
          } else if (segmentType) {
            // Store the value when we have valid profile insights
            sessionStorage.setItem('isNewUser', String(isNewUser))
          } else if (storedIsNewUser === null) {
            // First time and getUserInsights returned empty - store default value
            sessionStorage.setItem('isNewUser', String(isNewUser))
          }

          const isReturning = !finalIsNewUser

          // Fire analytics event after insights are set
          fireAndForget({
            event_type: 'track',
            event_action: `ID Check Segmentation Events - ${segmentType}`,
            ...handleTrackAndReactEvent(null, {}),
          })
          setIsReturningUser(isReturning)
        } catch (error) {
          // If there's an error, use sessionStorage value if it exists, otherwise default to true (new user)
          const storedIsNewUser = sessionStorage.getItem('isNewUser')
          const fallbackIsNewUser = storedIsNewUser !== null ? storedIsNewUser === 'true' : true
          setIsReturningUser(!fallbackIsNewUser)
          logger.error('Error fetching user insights:', { error })
        }
      }
      fetchInsights()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header?.siteEnv, isPageLoaded])

  const { dynamicPathname } = useAppRouter()

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.disclosureUrl = siteInfo?.disclosureDataUrl
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Guard against pages where there is no disclosureScriptUrl or disclosureDataUrl in siteInfo
    if (!siteInfo?.disclosureScriptUrl || !siteInfo?.disclosureDataUrl) return

    // Safe invoker: only call the function if the external script has defined it
    const invoke = () => {
      if (typeof window.processDisclosures === 'function') {
        window.processDisclosures()
      }
    }

    // run once immediately in case the script has already loaded
    invoke()

    // Keep things in sync as the DOM changes (e.g., content injected/updated later)
    const observer = new MutationObserver(() => {
      invoke()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    // Clean up on unmount or when dependencies change.
    return () => observer.disconnect()
  }, [siteInfo?.disclosureDataUrl, siteInfo?.disclosureScriptUrl])

  useEffect(() => {
    // Trigger Start of experiment (Variation or Control)
    const chatFlag = header?.siteEnv === SITE_ENVIRONMENTS.FDR ? FDR_CHATBOT : ACX_CHATBOT
    if (
      !Cookies.get('global_chatbot_experiment') &&
      (chatbotVariation === VARIATIONS_UPPER.CONTROL ||
        chatbotVariation === VARIATIONS_UPPER.VARIATION_A ||
        chatbotVariation === VARIATIONS_UPPER.VARIATION_B)
    ) {
      updateFeatureFlag(ACX_CHATBOT, chatbotVariation)
      fireAndForget({
        list_name: 'Chatbot Experiment Start',
        event_type: 'app_event',
        event_action: 'app_event',
        nav_link_section: CHATBOT_ANALYTICS_MAPPING[header?.siteEnv],
        track_event: 'chat_experiment_start',
        experiments: [
          createExperimentObject(
            CHATBOT_ANALYTICS_MAPPING[header?.siteEnv],
            chatFlag,
            chatbotVariation,
            'start'
          ),
        ],
      })
      Cookies.set('global_chatbot_experiment', true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotVariation, isReturningUser])

  const isHomePath = typeof window !== 'undefined' ? window.location.pathname === '/' : false
  const isLegalSubdomain =
    typeof window !== 'undefined' ? window.location.host.startsWith('legal') : false
  const enableMegaMenu =
    getFeatureFlag(FEATURE_TOGGLES[header?.siteEnv]?.ACX_WEB_ENABLE_MEGA_MENU) ===
    VARIATIONS_UPPER.VARIATION_A

  const {
    relativeWrapperStyle,
    headerContainerProps,
    footerContainerProps,
    footerStickyChatbotProps,
    footerStickyProps,
    chatGateProps,
  } = useLayoutChromeProps({
    header,
    footer,
    pageConfig,
    pageDisclosure,
    secondaryDisclosures,
    consentBanner,
    specificHeaderCtaDataFiltered,
    footerSticky,
    showPhoneNumber,
    publicProcessDotEnv,
    disabledRoutes,
    disabledElements,
    showProgressPage,
    customPhoneData,
    dynamicStickyNav,
    predefinedChatPrompts,
    phoneData,
    printWebAppSchema,
    printLocalBusinessSchema,
    associatedProduct,
    isHomePath,
    isConsentBannerVisible,
    setConsentBannerVisible,
    chatbotVariation,
    isReturningUser,
    onChatbotFlagReady,
  })

  return (
    <>
      <PhoneDataProvider phoneData={phoneData}>
        <LayoutProvider
          isMobileUA={isMobileUA}
          siteEnv={header?.siteEnv}
          siteInfo={siteInfo}
          leadId={leadId}
        >
          <ClientDashboardLoginProvider>
            <div style={relativeWrapperStyle}>
              {showHeader && (
                <>
                  <HeaderProvider>
                    <MemoizedHeaderContainer {...headerContainerProps} />
                  </HeaderProvider>

                  {header?.siteEnv === SITE_ENVIRONMENTS.ACHIEVE &&
                    !AHL_PZN_ROUTES_IGNORE.some((prefix) => dynamicPathname.startsWith(prefix)) && (
                      <DirectMailPzn />
                    )}
                </>
              )}

              <div
                className={classNames(styles['max-width-container'], 'group')}
                data-narrow={true}
                data-align-content-bottom={alignContentBottom}
              >
                <main
                  className={classNames(
                    ultramarine.variable,
                    'font-ultramarine',
                    makeMainStyle(
                      header.siteEnv,
                      showHeader,
                      showProgressPage,
                      header.showDebtConsultantCta,
                      isHomePath,
                      isLegalSubdomain,
                      enableMegaMenu
                    )
                  )}
                  id="main-layout"
                >
                  <LayoutMainChildren>{children}</LayoutMainChildren>
                </main>
              </div>
            </div>

            <ChatGate {...chatGateProps} />

            <BannerProvider>
              <ConsentBannerV2 />
              <div className="fixed bottom-0 w-full z-50">
                {(chatbotVariation === VARIATIONS_UPPER.VARIATION_A ||
                  chatbotVariation === VARIATIONS_UPPER.VARIATION_B) && (
                  <MemoizedFooterStickyChatbot {...footerStickyChatbotProps} />
                )}
                {/* FooterSticky: shown by default (null/Off/Control), and for FDR Variation_B alongside chatbot */}
                {(chatbotVariation === null ||
                  (chatbotVariation !== VARIATIONS_UPPER.VARIATION_A &&
                    chatbotVariation !== VARIATIONS_UPPER.VARIATION_B) ||
                  (header?.siteEnv === SITE_ENVIRONMENTS.FDR &&
                    chatbotVariation === VARIATIONS_UPPER.VARIATION_B)) && (
                  <MemoizedFooterSticky {...footerStickyProps} />
                )}
              </div>
            </BannerProvider>
            {showFooter && <MemoizedFooterContainer {...footerContainerProps} />}
            <ClientDashboardLoginOverlayGate
              footerContainerProps={footerContainerProps}
              applyFdrUrl={publicProcessDotEnv?.NEXT_PUBLIC_APPLY_FDR_URL}
              upscopeUrl={publicProcessDotEnv?.NEXT_PUBLIC_UPSCOPE_URL}
            />
          </ClientDashboardLoginProvider>
        </LayoutProvider>
      </PhoneDataProvider>
    </>
  )
}
